import { createInertiaApp } from '@inertiajs/react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { StrictMode } from 'react'
import '@mantine/core/styles.css'
import '@mantine/dates/styles.css'
import '@mantine/notifications/styles.css'
// TODO(#53): @mantine/{dropzone,tiptap} の CSS は admin 専用 (Dropzone =
// admin/menus の画像アップロード、TipTap = admin/posts のリッチエディタ) だが、
// 公開ページの entrypoint からも import されており LCP/FCP に影響する。
// admin/public で entrypoint を分けて、公開ページから外す予定。
import '@mantine/dropzone/styles.css'
import '@mantine/tiptap/styles.css'
// TODO(#53): @mantine/charts も admin 専用 (ダッシュボードのグラフ)。entrypoint 分離時に公開側から外す
import '@mantine/charts/styles.css'
import 'dayjs/locale/ja'
import '../styles/tokens.css'
import '../styles/utilities.css'
import '../styles/home-animations.css'
// アプリ内の CSS (CSS Modules 含む) をエントリに取り込み、<head> の <link> に載せる。
// 分離している理由と背景は styles/app-css.ts のコメントを参照 (issue #221)。
import '../styles/app-css'
import { ThemedRoot } from '../components/ThemedRoot'

// 本ファイルは React コンポーネント定義を含まず、import + createInertiaApp 呼び出しのみで
// 構成する。コンポーネント (ThemedRoot 等) を module top に置くと @vitejs/plugin-react が
// Fast Refresh 用の自己 import (`?t=<ts>`) を末尾に仕込み、createInertiaApp / createRoot が
// 二重評価される (= router listener 累積 → flash トースト二重発火) 問題を引き起こす。

void createInertiaApp({
  pages: "../pages",

  // 注: createInertiaApp の `strictMode` オプションは setup callback を提供した場合に
  // 無視される (Inertia 内部 `wrapWithStrictMode` がデフォルト経路のみで適用される)。
  // 自前 setup で StrictMode を明示的に被せて、dev mode の二重実行による副作用検知を
  // 有効にする。
  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    visitOptions: () => {
      return { queryStringArrayFormat: "brackets" }
    },
  },

  setup({ el, App, props }) {
    if (!el) return

    const tree = (
      <StrictMode>
        <ThemedRoot initialComponent={props.initialPage.component}>
          <App {...props} />
        </ThemedRoot>
      </StrictMode>
    )
    if (el.hasChildNodes()) {
      hydrateRoot(el, tree)
    } else {
      // SSR が有効な production / staging で root が空 (= SSR Node プロセスが落ちた、
      // SSR バンドルが render エラーで空文字を返した、等) のとき、CSR にフォールバック
      // するが SSR 失敗を静かに見逃すと SEO/LCP が劣化する。本番系のみ警告ログを出す。
      if (!import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Inertia] expected SSR-rendered DOM but found empty root, falling back to client render',
        )
      }
      createRoot(el).render(tree)
    }
  },
}).catch((error) => {
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
      'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  }
})
